import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { CATEGORY_LIST } from "../../services/CategoryModel";
import styles from "./CreateEventModal.module.css";

/* Modal for creating a new event, with automatic geocoding and stock photos */
export function CreateEventModal({ isOpen, onClose, onCreateEvent }) {
  if (!isOpen) return null;

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("tech");
  const [description, setDescription] = useState("");
  const [host, setHost] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [image, setImage] = useState("");
  const [loadingGeo, setLoadingGeo] = useState(false);

  // Geocodes the typed location string into lat/lng via the free
  // Nominatim (OpenStreetMap) API, appending ", Dublin, Ireland" when the
  // user didn't already mention Dublin, since this app is Dublin-focused.
  // Falls back to Dublin's city-center coordinates if geocoding fails or
  // finds nothing, so event creation never blocks on this lookup.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !description || !location) return;

    setLoadingGeo(true);
    let lat = 53.3498;
    let lng = -6.2603;

    try {
      const searchQuery = location.toLowerCase().includes("dublin")
        ? location
        : `${location}, Dublin, Ireland`;

      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery
        )}`
      );
      const data = await res.json();

      if (data && data.length > 0) {
        lat = parseFloat(data[0].lat);
        lng = parseFloat(data[0].lon);
      }
    } catch (error) {
      console.warn("Geocoding fallback used.", error);
    } finally {
      setLoadingGeo(false);
    }

    // Stock photo used when the user doesn't provide their own image URL,
    // picked by category so the event card still looks relevant.
    const categoryImages = {
      tech: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=500&auto=format&fit=crop&q=60",
      social:
        "https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=500&auto=format&fit=crop&q=60",
      sports:
        "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=500&auto=format&fit=crop&q=60",
      arts: "https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=500&auto=format&fit=crop&q=60",
      food: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=500&auto=format&fit=crop&q=60",
      outdoors:
        "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=500&auto=format&fit=crop&q=60",
      music:
        "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=500&auto=format&fit=crop&q=60",
      games:
        "https://images.unsplash.com/photo-1612287233202-b51e3ed615c5?w=500&auto=format&fit=crop&q=60",
    };

    onCreateEvent({
      title,
      category,
      description,
      host: host || "Meetly Community",
      date: date || "Upcoming",
      location,
      lat,
      lng,
      image: image || categoryImages[category] || categoryImages.tech,
      color: "bg-pink-500",
      tags: [category, "Meetup"],
    });
    onClose();
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.headerRow}>
          <h3 className={styles.title}>Create New Event</h3>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div>
            <label className={styles.fieldLabel}>Event Title</label>
            <input
              type="text"
              required
              placeholder="e.g. Sunset Boardgames"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={styles.textInput}
            />
          </div>

          <div>
            <label className={styles.fieldLabel}>Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={styles.textInput}
            >
              {CATEGORY_LIST.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={styles.fieldLabel}>Description</label>
            <textarea
              required
              rows="3"
              placeholder="What is this event about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={styles.textInput}
            />
          </div>

          <div>
            <label className={styles.fieldLabel}>Host / Organizer</label>
            <input
              type="text"
              placeholder="e.g. Dublin Boardgamers"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              className={styles.textInput}
            />
          </div>

          <div>
            <label className={styles.fieldLabel}>Date & Time</label>
            <input
              type="text"
              placeholder="e.g. Saturday, 4:00 PM"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={styles.textInput}
            />
          </div>

          <div>
            <label className={styles.fieldLabel}>Location</label>
            <input
              type="text"
              placeholder="e.g. St. Stephen's Green, Dublin"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
              className={styles.textInput}
            />
          </div>

          <button
            type="submit"
            disabled={loadingGeo}
            className={styles.submitButton}
          >
            {loadingGeo && (
              <Loader2 className={styles.spinnerIcon} size={18} />
            )}
            {loadingGeo ? "Finding location..." : "Publish Event"}
          </button>
        </form>
      </div>
    </div>
  );
}
